/**
 * Remembering the folder between visits — ported from the DI-T1 spike
 * (`_SPIKES/browser-intake-benchmarks/src/handleStore.ts`), DI-D13 c3: "one
 * folder grant, remembered in IndexedDB and re-requested on return".
 *
 * A `FileSystemDirectoryHandle` is structured-cloneable, so IndexedDB holds it.
 * On a later visit the handle comes back and only the PERMISSION has to be
 * re-checked — which Chrome grants without a prompt when the pilot chose to
 * allow the site on every visit. Product change against the spike: this story
 * only reads, so the permission asked for is `read`, not `readwrite`.
 * DI-5-1 — the write-back asks `readwrite` (`permissionOf` / `reRequest`
 * take the mode; `read` stays the default for every reader).
 */
import { HANDLES, tx } from "../session/db.js";

interface Permissioned {
  queryPermission?: (d: {
    mode: "read" | "readwrite";
  }) => Promise<PermissionState>;
  requestPermission?: (d: {
    mode: "read" | "readwrite";
  }) => Promise<PermissionState>;
}

export async function remember(key: string, handle: unknown): Promise<void> {
  await tx(HANDLES, "readwrite", (s) => s.put(handle, key));
}

export interface RecalledHandle<H> {
  handle: H;
  /** "unknown" when the stored object cannot answer (e.g. a restored test double). */
  permission: PermissionState | "unknown";
}

/**
 * Returns the stored handle and whether it may still be read. Never prompts:
 * `requestPermission()` needs a user gesture, so the caller decides.
 */
export async function recall<H>(
  key: string,
): Promise<RecalledHandle<H> | null> {
  const handle = await tx<unknown>(HANDLES, "readonly", (s) =>
    s.get(key),
  ).catch(() => undefined);
  if (!handle) return null;
  const h = handle as Permissioned;
  if (typeof h.queryPermission !== "function")
    return { handle: handle as H, permission: "unknown" };
  try {
    return {
      handle: handle as H,
      permission: await h.queryPermission({ mode: "read" }),
    };
  } catch {
    return null;
  }
}

/**
 * DI-3-S8-FE — may this tab read `handle` NOW, without a gesture? The
 * `queryPermission` answer ("granted" / "prompt" / "denied"); "unknown" when
 * the object cannot answer (a test double), "denied" when the query throws
 * (a handle whose folder is gone). Never prompts.
 */
export async function permissionOf(
  handle: unknown,
  mode: "read" | "readwrite" = "read",
): Promise<PermissionState | "unknown"> {
  const h = handle as Permissioned;
  if (typeof h.queryPermission !== "function") return "unknown";
  try {
    return await h.queryPermission({ mode });
  } catch {
    return "denied";
  }
}

/**
 * Asks for the permission back. Must be called from a user gesture.
 * DI-5-1 — `mode: "readwrite"` for the write-back (asked ONCE, at approve).
 */
export async function reRequest(
  handle: unknown,
  mode: "read" | "readwrite" = "read",
): Promise<PermissionState | "unknown"> {
  const h = handle as Permissioned;
  if (typeof h.requestPermission !== "function") return "unknown";
  return h.requestPermission({ mode });
}

/** Drops a remembered handle, so the next pick really opens the picker. */
export async function forget(key: string): Promise<void> {
  await tx(HANDLES, "readwrite", (s) => s.delete(key));
}

// ── DI-3-S11-FE — the field-data ROOT ─────────────────────────────────────

/**
 * The key of the ONE field-data root per browser: the folder that holds the
 * pilot's card folders, picked once on the Upload page (mode `read`). A
 * fallback, never the first source: a session record's own handle wins when
 * it is granted (in a running Chrome every remembered handle answers
 * "granted" — measured 2026-10-05, story §0), the root is for a restarted
 * browser and for drops that were never granted in this one.
 */
export const ROOT_KEY = "root";

/**
 * The surface of the root the lookup needs (a real directory handle has it).
 * DI-3-S16-FE — `values` (the breadth-first search lists the root's
 * directories) and `resolve` (`FileSystemDirectoryHandle.resolve(descendant)`:
 * the path of a handle under it, `null` when it is not under it). `resolve`
 * is not in every TS DOM lib, so it is typed here, like
 * `BeforeInstallPromptEvent` (DI-3-S11-FE) — and its absence is guarded.
 */
export interface RootDirLike {
  kind?: "directory";
  name: string;
  getDirectoryHandle?: (name: string) => Promise<unknown>;
  values?: () => AsyncIterable<{ kind: string; name: string }>;
  resolve?: (possibleDescendant: unknown) => Promise<string[] | null>;
}

export async function rememberRoot(handle: unknown): Promise<void> {
  await remember(ROOT_KEY, handle);
}

/** The stored root and its permission now; null when none. Never prompts. */
export async function recallRoot<H = RootDirLike>(
  deps: { recall?: typeof recall } = {},
): Promise<RecalledHandle<H> | null> {
  return (deps.recall ?? recall)<H>(ROOT_KEY).catch(() => null);
}

export async function forgetRoot(): Promise<void> {
  await forget(ROOT_KEY);
}

/**
 * DI-3-S16-FE — how far below the root a drop folder is searched for by
 * name (Christian 2026-10-06: day or park, then turbine, then the images —
 * a folder at most three levels down), and after how many listed
 * directories the search gives up (then the picker asks, as before).
 */
export const ROOT_SEARCH_DEPTH = 3;
export const ROOT_SEARCH_MAX_DIRS = 2_000;

/** What the root answers for a drop folder (`resolveUnderRoot`). */
export type RootLookup<H> =
  /**
   * The folder; `path` = the names from the root down to it (it included);
   * `name` = its CURRENT name (the last of `path`). DI-3-S18-FE — `byContent`:
   * found by the record's files, not by its name (a folder renamed on disk) —
   * the caller heals the record (`renameSessionFolder`).
   */
  | {
      kind: "found";
      handle: H;
      rootName: string;
      path: string[];
      name: string;
      byContent?: true;
    }
  /** Several folders of that name, and the file names did not pick one. */
  | { kind: "ambiguous"; rootName: string; count: number }
  /** A granted root that holds no such folder (within the search's bounds). */
  | { kind: "absent"; rootName: string };

export interface RootLookupOptions {
  /**
   * The path recorded at drop time (`SessionRecord.rootPath`, from
   * `root.resolve(dropHandle)`): walked step by step, no search.
   */
  rootPath?: readonly string[] | undefined;
  /**
   * The record's files as `name|size` keys (`rowHashes`, DI-3-S15-FE): among
   * several folders of the name, the one that holds every key wins.
   */
  keys?: ReadonlySet<string> | undefined;
  /**
   * Even ONE folder of the name must hold every key — DI-3-S11-FE's "two
   * live records share the name" (the name alone cannot tell them apart).
   */
  verify?: boolean | undefined;
  /** The search runs once per page load per this key (a record's id). */
  cacheKey?: string | undefined;
}

export interface RootLookupDeps {
  recall?: typeof recall;
  /** The `name|size` keys of the image files under a candidate folder. */
  listKeys?: (dir: unknown) => Promise<Set<string>>;
}

interface DirLike {
  kind?: string;
  name: string;
  values?: () => AsyncIterable<{ kind: string; name: string }>;
  getDirectoryHandle?: (name: string) => Promise<unknown>;
}

interface Candidate {
  handle: unknown;
  path: string[];
}

interface SearchResult {
  candidates: Candidate[];
  /** False when the directory bound stopped the walk. */
  complete: boolean;
  /** Directories listed (tests: the bound). */
  listed: number;
}

const searches = new Map<string, Promise<SearchResult>>();
/** DI-3-S18-FE — the content fallback's answers, once per page load per record. */
const contentSearches = new Map<string, Promise<Candidate | null>>();

/** Tests: forget every search of this page load. */
export function resetRootSearch(): void {
  searches.clear();
  contentSearches.clear();
}

/**
 * Breadth-first over the root's DIRECTORIES (a file entry is skipped, never
 * opened), depth ≤ `ROOT_SEARCH_DEPTH`, at most `ROOT_SEARCH_MAX_DIRS`
 * directories listed; every directory named `folderName` is a candidate (the
 * walk does not descend into a candidate).
 */
async function searchByName(
  root: DirLike,
  folderName: string,
): Promise<SearchResult> {
  const candidates: Candidate[] = [];
  let level: { dir: DirLike; path: string[] }[] = [{ dir: root, path: [] }];
  let listed = 0;
  for (let depth = 1; depth <= ROOT_SEARCH_DEPTH && level.length > 0; depth++) {
    const next: { dir: DirLike; path: string[] }[] = [];
    for (const { dir, path } of level) {
      if (typeof dir.values !== "function") continue;
      if (listed >= ROOT_SEARCH_MAX_DIRS)
        return { candidates, complete: false, listed };
      listed++;
      try {
        for await (const child of dir.values()) {
          if (child.kind !== "directory") continue;
          const childPath = [...path, child.name];
          if (child.name === folderName)
            candidates.push({ handle: child, path: childPath });
          else if (depth < ROOT_SEARCH_DEPTH)
            next.push({ dir: child, path: childPath });
        }
      } catch {
        // an unreadable directory is skipped
      }
    }
    level = next;
  }
  return { candidates, complete: true, listed };
}

/** The `name|size` keys of the image files under `dir` (recursive). */
async function imageKeysOf(dir: unknown): Promise<Set<string>> {
  const out = new Set<string>();
  const walk = async (d: unknown): Promise<void> => {
    const it = (
      d as { values?: () => AsyncIterable<{ kind: string; name: string }> }
    ).values;
    if (typeof it !== "function") return;
    for await (const h of it.call(d)) {
      if (h.kind === "directory") await walk(h);
      else if (/\.jpe?g$/i.test(h.name)) {
        const getFile = (h as { getFile?: () => Promise<File> }).getFile;
        if (typeof getFile !== "function") continue;
        const f = await getFile.call(h);
        out.add(`${h.name}|${String(f.size)}`);
      }
    }
  };
  await walk(dir);
  return out;
}

async function walkPath(
  root: DirLike,
  path: readonly string[],
): Promise<unknown> {
  let dir: unknown = root;
  for (const name of path) {
    const lookup = (dir as DirLike).getDirectoryHandle;
    if (typeof lookup !== "function") return null;
    dir = await lookup.call(dir, name);
  }
  return dir;
}

/**
 * DI-3-S11-FE — the drop's folder by NAME under a stored root whose
 * permission is already "granted". DI-3-S16-FE widens it to THREE levels
 * (story §1.6), cheapest first:
 *   1. the path recorded at drop time (`opts.rootPath`): walked with
 *      `getDirectoryHandle`, no search; a step that fails falls to 2;
 *   2. the search by name (`searchByName`: breadth-first, directories only,
 *      depth ≤ 3, ≤ 2 000 directories, once per page load per `cacheKey`):
 *      one folder of the name → it (unless `verify`); several → each one's
 *      image files are listed and the ONE holding every `keys` entry wins;
 *      none or more than one → "ambiguous" (the picker). A walk the bound
 *      stopped only accepts a candidate that holds every key.
 *   3. DI-3-S18-FE — only when 1 and 2 answered "absent": the folder by the
 *      record's FILES (`byContent` — beside the recorded place, then one
 *      `getFileHandle(<first file>)` probe per directory of the same bounded
 *      walk, a hit proved by every key) → "found" with `byContent` and the
 *      folder's CURRENT name (a folder renamed on disk); the caller heals.
 * A root without `values` (a double) is asked one level down only, as S11.
 * Null when there is no root or its permission is not "granted" ("prompt" /
 * "denied" / "unknown"); "absent" when the granted root holds no such
 * folder. It NEVER calls `requestPermission` — there is no gesture here;
 * re-asking for the root is the Upload page's button.
 */
export async function resolveUnderRoot<H = unknown>(
  folderName: string,
  deps: RootLookupDeps = {},
  opts: RootLookupOptions = {},
): Promise<RootLookup<H> | null> {
  const root = await recallRoot<DirLike>(deps);
  if (root?.permission !== "granted") return null;
  const r = root.handle;
  const rootName = r.name;
  const absent: RootLookup<H> = { kind: "absent", rootName };
  // 1. the recorded path
  if (opts.rootPath && opts.rootPath.length > 0) {
    const at = await walkPath(r, opts.rootPath).catch(() => null);
    if (at)
      return {
        kind: "found",
        handle: at as H,
        rootName,
        path: [...opts.rootPath],
        name: opts.rootPath.at(-1) ?? folderName,
      };
  }
  if (!folderName) return absent;
  // S11's one level, for a root that cannot list itself
  if (typeof r.values !== "function") {
    if (typeof r.getDirectoryHandle !== "function") return absent;
    try {
      const handle = await r.getDirectoryHandle.call(r, folderName);
      return {
        kind: "found",
        handle: handle as H,
        rootName,
        path: [folderName],
        name: folderName,
      };
    } catch {
      return absent;
    }
  }
  // 2. the search by name, once per page load per record
  const cacheKey = [rootName, opts.cacheKey ?? "", folderName].join("\n");
  let search = searches.get(cacheKey);
  if (!search) {
    search = searchByName(r, folderName).catch(() => ({
      candidates: [],
      complete: true,
      listed: 0,
    }));
    searches.set(cacheKey, search);
  }
  const { candidates, complete } = await search;
  // DI-3-S18-FE — "absent" by name and path: the record's files decide
  if (candidates.length === 0) return byContent(r, deps, opts, absent);
  const only = candidates.length === 1 ? candidates[0] : undefined;
  if (only && complete && opts.verify !== true)
    return {
      kind: "found",
      handle: only.handle as H,
      rootName,
      path: only.path,
      name: folderName,
    };
  // no winner: ONE folder the bound left unconfirmed is "not found" (the
  // picker); otherwise the name cannot pick one ("ambiguous", the picker)
  const unpicked: RootLookup<H> =
    candidates.length === 1 && opts.verify !== true
      ? absent
      : { kind: "ambiguous", rootName, count: candidates.length };
  const keys = opts.keys;
  if (!keys || keys.size === 0) return unpicked;
  // DI-3-S18-FE — a lone same-name folder the bound left unconfirmed is
  // "absent": the record's files may still find the folder elsewhere
  const orContent = (): Promise<RootLookup<H>> =>
    unpicked.kind === "absent"
      ? byContent(r, deps, opts, absent)
      : Promise.resolve(unpicked);
  const listKeys = deps.listKeys ?? imageKeysOf;
  const winners: Candidate[] = [];
  for (const c of candidates) {
    const held = await listKeys(c.handle).catch(() => new Set<string>());
    if ([...keys].every((k) => held.has(k))) winners.push(c);
  }
  const winner = winners.length === 1 ? winners[0] : undefined;
  return winner
    ? {
        kind: "found",
        handle: winner.handle as H,
        rootName,
        path: winner.path,
        name: folderName,
      }
    : orContent();
}

// ── DI-3-S18-FE — the content fallback: a folder renamed on disk ──────────

/**
 * The record's FIRST file — the first of `keys` (`rootFolder.ts`
 * `recordFileKeys`: the first set's first `rowHashes` key, `name|size`) —
 * the one name the probe asks every directory for.
 */
export function firstFileOf(
  keys: ReadonlySet<string> | undefined,
): { name: string; size: number } | null {
  const first = keys ? [...keys][0] : undefined;
  if (!first) return null;
  const bar = first.lastIndexOf("|");
  if (bar <= 0) return null;
  const size = Number(first.slice(bar + 1));
  return Number.isFinite(size) ? { name: first.slice(0, bar), size } : null;
}

/** The probe: ONE `getFileHandle(<first file>)` on a directory, + its size. */
async function holdsFile(
  dir: unknown,
  file: { name: string; size: number },
): Promise<boolean> {
  const get = (dir as { getFileHandle?: (name: string) => Promise<unknown> })
    .getFileHandle;
  if (typeof get !== "function") return false;
  try {
    const fh = (await get.call(dir, file.name)) as {
      getFile?: () => Promise<File>;
    };
    if (typeof fh.getFile !== "function") return false;
    return (await fh.getFile.call(fh)).size === file.size;
  } catch {
    return false;
  }
}

/** S16's key check: the folder holds EVERY `name|size` key of the record. */
async function holdsEveryKey(
  dir: unknown,
  keys: ReadonlySet<string>,
  deps: RootLookupDeps,
): Promise<boolean> {
  const held = await (deps.listKeys ?? imageKeysOf)(dir).catch(
    () => new Set<string>(),
  );
  return [...keys].every((k) => held.has(k));
}

/**
 * DI-3-S18-FE — the record's folder by its FILES, never by its name; asked
 * only after the recorded path and the name answered "absent":
 *   a. a recorded path whose last step is gone (the folder renamed where it
 *      sits): the folders beside it — ONE listing of its parent — and the one
 *      holding every key;
 *   b. the name search's bounded walk (breadth-first, directories only, depth
 *      ≤ `ROOT_SEARCH_DEPTH`, ≤ `ROOT_SEARCH_MAX_DIRS` directories listed):
 *      each directory is asked ONE `getFileHandle(<first file>)` + its size;
 *      a hit must then hold every key.
 * Exactly ONE folder holding every key is the answer ("found", `byContent`,
 * its CURRENT name and path); none, several, or a walk the bound stopped
 * without one → "absent" (the picker, as before). A partial match (the first
 * file only, some of the keys) is never an answer. Never a prompt; once per
 * page load per record (`cacheKey`).
 */
async function byContent<H>(
  root: DirLike,
  deps: RootLookupDeps,
  opts: RootLookupOptions,
  absent: RootLookup<H>,
): Promise<RootLookup<H>> {
  const keys = opts.keys;
  const first = firstFileOf(keys);
  if (!keys || !first || typeof root.values !== "function") return absent;
  const cacheKey = [
    root.name,
    opts.cacheKey ?? "",
    (opts.rootPath ?? []).join("/"),
    `${first.name}|${String(first.size)}`,
  ].join("\n");
  let search = contentSearches.get(cacheKey);
  if (!search) {
    search = searchByContent(root, first, keys, deps, opts.rootPath).catch(
      () => null,
    );
    contentSearches.set(cacheKey, search);
  }
  const hit = await search;
  if (!hit) return absent;
  return {
    kind: "found",
    handle: hit.handle as H,
    rootName: root.name,
    path: hit.path,
    name: hit.path.at(-1) ?? "",
    byContent: true,
  };
}

async function searchByContent(
  root: DirLike,
  first: { name: string; size: number },
  keys: ReadonlySet<string>,
  deps: RootLookupDeps,
  recorded: readonly string[] | undefined,
): Promise<Candidate | null> {
  // a. beside the recorded place
  if (recorded && recorded.length > 0) {
    const parentPath = recorded.slice(0, -1);
    const gone = recorded.at(-1);
    const parent = (await walkPath(root, parentPath).catch(
      () => null,
    )) as DirLike | null;
    if (parent && typeof parent.values === "function") {
      const winners: Candidate[] = [];
      try {
        for await (const child of parent.values()) {
          if (child.kind !== "directory" || child.name === gone) continue;
          if (await holdsEveryKey(child, keys, deps))
            winners.push({ handle: child, path: [...parentPath, child.name] });
        }
      } catch {
        // an unreadable parent: the walk below
      }
      if (winners.length === 1) return winners[0] ?? null;
      if (winners.length > 1) return null;
    }
  }
  // b. the bounded walk, one probe per directory
  const hits: Candidate[] = [];
  let level: { dir: DirLike; path: string[] }[] = [{ dir: root, path: [] }];
  let listed = 0;
  for (let depth = 1; depth <= ROOT_SEARCH_DEPTH && level.length > 0; depth++) {
    const next: { dir: DirLike; path: string[] }[] = [];
    for (const { dir, path } of level) {
      if (typeof dir.values !== "function") continue;
      if (listed >= ROOT_SEARCH_MAX_DIRS) break;
      listed++;
      try {
        for await (const child of dir.values()) {
          if (child.kind !== "directory") continue;
          const childPath = [...path, child.name];
          if (await holdsFile(child, first))
            hits.push({ handle: child, path: childPath });
          else if (depth < ROOT_SEARCH_DEPTH)
            next.push({ dir: child, path: childPath });
        }
      } catch {
        // an unreadable directory is skipped
      }
    }
    level = listed >= ROOT_SEARCH_MAX_DIRS ? [] : next;
  }
  const winners: Candidate[] = [];
  for (const h of hits)
    if (await holdsEveryKey(h.handle, keys, deps)) winners.push(h);
  return winners.length === 1 ? (winners[0] ?? null) : null;
}

/**
 * DI-3-S16-FE — the path of a dropped folder under the granted root, asked
 * at drop time: `root.resolve(dropHandle)` (["2026-09-01", "Park Nord",
 * "WTG-05"]). Null when there is no granted root, the browser has no
 * `resolve`, the folder is not under the root (or IS the root), or anything
 * throws. Never prompts.
 */
export async function rootPathOf(
  dropHandle: unknown,
  deps: { recall?: typeof recall } = {},
): Promise<string[] | null> {
  if (!dropHandle) return null;
  try {
    const root = await recallRoot(deps);
    if (root?.permission !== "granted") return null;
    const resolve = root.handle.resolve;
    if (typeof resolve !== "function") return null;
    const path = await resolve.call(root.handle, dropHandle);
    return Array.isArray(path) && path.length > 0 ? path : null;
  } catch {
    return null;
  }
}
